import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Wallet, Info } from 'lucide-react';
import { api } from '../services/api';
import EntityPicker from './ui/EntityPicker';
import type { ConsumptionPreview as Preview } from '../types/billing';
import { formatYearMonth } from '../utils/format';

export interface ConsumptionChoice {
  hour_bank_id?: string;
  override_reason?: string;
}

interface Props {
  ticketId: string;
  /** ISO or datetime-local strings. */
  startTime?: string;
  endTime?: string;
  workLogId?: string;
  /** Explicit billable choice (undefined = automatic from engagement rules). */
  billable?: boolean;
  value: ConsumptionChoice;
  onChange: (next: ConsumptionChoice) => void;
  /** Reports whether saving would be refused (blocked and no usable override). */
  onBlockedChange?: (blocked: boolean) => void;
  /** Reports the automatically resolved billability (decision 33). */
  onAutoBillable?: (billable: boolean) => void;
}

/**
 * Decisions 24/28/80 — shows, before saving, which hour bank / contract quota
 * a work log will draw from, the balance, and whether it would be blocked.
 * Users may pick another purchase; authorized users may override a block
 * with a mandatory reason. The server re-checks everything on save.
 */
export default function ConsumptionPreview({
  ticketId,
  startTime,
  endTime,
  workLogId,
  billable,
  value,
  onChange,
  onBlockedChange,
  onAutoBillable,
}: Props) {
  const { t } = useTranslation();
  const minutes = useMemo(() => {
    if (!startTime || !endTime) return 0;
    const ms = new Date(endTime).getTime() - new Date(startTime).getTime();
    return Number.isFinite(ms) && ms > 0 ? Math.round(ms / 60000) : 0;
  }, [startTime, endTime]);

  const { data, isFetching } = useQuery({
    queryKey: ['consumption-preview', ticketId, minutes, startTime, value.hour_bank_id, workLogId, billable],
    queryFn: () =>
      api
        .getConsumptionPreview({
          ticket_id: ticketId,
          minutes,
          start_time: startTime ? new Date(startTime).toISOString() : undefined,
          hour_bank_id: value.hour_bank_id || undefined,
          work_log_id: workLogId,
          billable,
        })
        .then((r) => r.data as Preview),
    enabled: !!ticketId && minutes > 0,
    staleTime: 10_000,
  });

  // D-266 sec.1: beyond the quota / bank the log is SAVED (marked, waiting) - only an expired chosen bank blocks saving.
  const blocked = !!data?.would_block && !data.over_quota && !(data.can_override && (value.override_reason ?? '').trim().length >= 2);
  useEffect(() => {
    onBlockedChange?.(blocked);
  }, [blocked, onBlockedChange]);
  useEffect(() => {
    if (data && billable === undefined) onAutoBillable?.(data.billable);
  }, [data, billable, onAutoBillable]);

  // D-266 sec.3 (EQS-03): a date before the engagement's start is allowed - only a warning.
  const beforeStart = data?.before_engagement_start ? (
    <div className="text-xs text-warning-700 flex items-center gap-1.5" data-testid="before-engagement-start">
      <AlertTriangle size={14} /> {t('consumption.beforeStart')}
    </div>
  ) : null;

  if (!data || data.kind === 'none' || !data.billable) {
    if (data && !data.billable && data.kind !== 'none') {
      return (
        <>
          <div className="text-xs text-surface-500 flex items-center gap-1.5">
            <Info size={14} /> {t('consumption.notBillable')}
          </div>
          {beforeStart}
        </>
      );
    }
    return beforeStart;
  }

  return (
    <div className={`rounded-lg border px-3 py-2 text-sm space-y-2 ${data.would_block ? (data.over_quota ? 'border-warning-200 bg-warning-50' : 'border-danger-200 bg-danger-50') : 'border-surface-200 bg-surface-50'}`}>
      {data.kind === 'hour_bank' && (
        <>
          <div className="flex items-center gap-2 text-surface-700">
            <Wallet size={16} className="text-accent-text" />
            <span>
              {data.bank
                ? t('consumption.bankLine', { name: data.bank.name, available: data.available ?? 0 })
                : t('consumption.noBank')}
            </span>
            {isFetching && <span className="text-xs text-surface-500">…</span>}
          </div>
          {data.allocations.length > 1 && (
            <div className="text-xs text-surface-600">
              {t('consumption.split', {
                parts: data.allocations.map((a) => `${a.bank_name}: ${a.hours}`).join(' · '),
              })}
            </div>
          )}
          {data.banks.length > 1 && (
            <div>
              <label className="text-xs text-surface-500 mb-1 block">{t('consumption.chooseBank')}</label>
              {/* DQ-449 (D-203/D-207): a bank picker with search. */}
              <EntityPicker
                emptyLabel={t('consumption.autoBank')}
                options={data.banks.map((b) => ({
                  value: b.id,
                  label: `${b.name} — ${t('consumption.bankOption', { balance: b.balance, expiry: b.expiry_date ?? '—' })}${b.expired ? ` (${t('consumption.expired')})` : ''}`,
                  disabled: !b.consumable,
                }))}
                value={value.hour_bank_id ?? ''}
                onChange={(v) => onChange({ ...value, hour_bank_id: v || undefined })}
              />
            </div>
          )}
        </>
      )}

      {data.kind === 'service_contract' && data.contract && (
        <div className="text-surface-700">
          {data.contract.quota == null
            ? t('consumption.contractNoQuota')
            : t('consumption.contractLine', {
                month: formatYearMonth(data.contract.month),
                consumed: data.contract.consumed_hours,
                quota: data.contract.quota,
                remaining: data.contract.remaining ?? 0,
              })}
        </div>
      )}

      {beforeStart}

      {data.requires_approval && (
        <div className="text-xs text-warning-700 flex items-center gap-1.5">
          <Info size={14} /> {t('consumption.requiresApproval')}
        </div>
      )}

      {data.would_block && data.over_quota && (
        <div className="space-y-2">
          <div className="flex items-start gap-2 text-warning-700">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            <span>{t(data.kind === 'hour_bank' ? 'consumption.bankLockedWillSave' : 'consumption.overQuotaWillSave')}</span>
          </div>
          {data.can_override && (
            <div>
              <label className="text-xs text-surface-600 mb-1 block">{t('consumption.overrideOptional')}</label>
              <textarea
                className="input text-sm w-full"
                rows={2}
                value={value.override_reason ?? ''}
                onChange={(e) => onChange({ ...value, override_reason: e.target.value })}
                placeholder={t('consumption.overridePlaceholder')}
              />
            </div>
          )}
        </div>
      )}

      {data.would_block && !data.over_quota && (
        <div className="space-y-2">
          <div className="flex items-start gap-2 text-danger-700">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            <span>{data.message}</span>
          </div>
          {data.can_override ? (
            <div>
              <label className="text-xs text-danger-700 mb-1 block">{t('consumption.overrideReason')}</label>
              <textarea
                className="input text-sm w-full"
                rows={2}
                value={value.override_reason ?? ''}
                onChange={(e) => onChange({ ...value, override_reason: e.target.value })}
                placeholder={t('consumption.overridePlaceholder')}
              />
            </div>
          ) : (
            <div className="text-xs text-danger-700">{t('consumption.askManager')}</div>
          )}
        </div>
      )}
    </div>
  );
}
